import { type OpenShift } from "@clipboard-health/contract-worker-app-bff";
import { useCallback, useState } from "react";

import { useAutoProgression } from "./useAutoProgression";

interface UseShiftRecommendationsNavigationProps {
  recommendedShifts: OpenShift[];
  onClose?: () => void;
  autoProgressDuration?: number; // in milliseconds
}

interface UseShiftRecommendationsNavigationReturn {
  currentIndex: number;
  previousIndex: number;
  currentShift: OpenShift | undefined;
  lastIndex: number;
  isFirst: boolean;
  isLast: boolean;
  goNext: () => void;
  goPrevious: () => void;
  reset: () => void;
  progress: number;
  isPaused: boolean;
  pause: () => void;
  resume: () => void;
  resetProgress: () => void;
}

export function useShiftRecommendationsNavigation(
  props: UseShiftRecommendationsNavigationProps
): UseShiftRecommendationsNavigationReturn {
  const { recommendedShifts, onClose, autoProgressDuration = 5000 } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(0);

  const goNext = useCallback(() => {
    setCurrentIndex((previous) => {
      setPreviousIndex(previous);
      const nextIndex = previous + 1;

      // If we're going beyond the last index, close the modal
      if (nextIndex > recommendedShifts.length - 1) {
        onClose?.();
        return previous; // Stay on current index
      }

      return nextIndex;
    });
  }, [recommendedShifts.length, onClose]);

  const {
    progress,
    isPaused,
    pause,
    resume,
    reset: resetCurrentProgress,
  } = useAutoProgression({
    isActive: recommendedShifts.length > 0,
    duration: autoProgressDuration,
    onComplete: goNext,
    key: currentIndex, // Restart progress when index changes
  });

  const reset = useCallback(() => {
    setCurrentIndex(0);
    setPreviousIndex(0);
    resetCurrentProgress();
  }, [resetCurrentProgress]);

  const resetProgress = useCallback(() => {
    resetCurrentProgress();
  }, [resetCurrentProgress]);

  const currentShift = recommendedShifts[currentIndex];
  const isFirst = currentIndex === 0;
  const lastIndex = recommendedShifts.length - 1;
  const isLast = currentIndex === lastIndex;

  const goPrevious = useCallback(() => {
    setCurrentIndex((previous) => {
      setPreviousIndex(previous);
      const nextIndex = previous - 1;

      // If we're going before the first index, close the modal
      if (nextIndex < 0) {
        onClose?.();
        return previous; // Stay on current index
      }

      return nextIndex;
    });
  }, [onClose]);

  // Override goNext to also reset progress
  const goNextWithReset = useCallback(() => {
    setCurrentIndex((previous) => {
      setPreviousIndex(previous);
      const nextIndex = previous + 1;

      // If we're going beyond the last index, close the modal
      if (nextIndex > lastIndex) {
        onClose?.();
        return previous; // Stay on current index
      }

      return nextIndex;
    });
  }, [lastIndex, onClose]);

  return {
    currentIndex,
    previousIndex,
    currentShift,
    lastIndex,
    isFirst,
    isLast,
    goNext: goNextWithReset,
    goPrevious,
    reset,
    progress,
    isPaused,
    pause,
    resume,
    resetProgress,
  };
}
